/* eslint-disable */
import { useState } from "react";
import { T, useTheme } from "../tokens";
import { Row } from "./WireframePrimitives";
import ResponsiveShell from "./ResponsiveShell";

const SA_NAV_ITEMS = [
  ["💳", "subscriptions", "Subscriptions"],
  ["📦", "plans", "Plan Manager"],
  ["💸", "cashfree", "Cashfree Links"],
  ["👥", "users", "Users"],
  ["⚙️", "app_config", "App Config"],
  ["🚩", "flags", "Feature Flags"],
  ["📋", "audit", "Audit Log"],
];

export function SASidebar({ dark, active = "subscriptions", collapsed = false }) {
  const th = useTheme(dark);
  const sidebarWidth = collapsed ? 58 : 130;

  return (
    <div
      style={{
        width: sidebarWidth,
        flexShrink: 0,
        alignSelf: "stretch",
        background: th.sidebar,
        borderRight: `1px solid ${th.border}`,
        padding: "8px 0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!collapsed && (
        <div style={{ padding: "4px 8px 8px", borderBottom: `1px solid ${th.border}`, marginBottom: 6 }}>
          <div style={{ fontSize: 8, color: "#475569", fontWeight: 600, letterSpacing: "0.06em", marginBottom: 4 }}>
            APP
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 6px",
              background: `${T.brand}18`,
              borderRadius: 6,
              border: `1px solid ${T.brand}33`,
            }}
          >
            <span style={{ fontSize: 11 }}>🏪</span>
            <div>
              <div style={{ fontSize: 9, fontWeight: 600, color: T.brand }}>ZiniHub</div>
              <div style={{ fontSize: 7, color: "#475569" }}>Multi-biz platform</div>
            </div>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.brand, marginLeft: "auto" }} />
          </div>
        </div>
      )}
      {collapsed && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 10px" }}>
          <span style={{ fontSize: 14 }}>🏪</span>
        </div>
      )}
      <div style={{ padding: collapsed ? "4px 4px" : "4px 6px" }}>
        {!collapsed && (
          <div style={{ fontSize: 8, color: "#475569", fontWeight: 600, letterSpacing: "0.06em", marginBottom: 5, paddingLeft: 2 }}>
            MODULES
          </div>
        )}
        {SA_NAV_ITEMS.map(([icon, id, label]) => {
          const isActive = active === id;
          return (
            <div
              key={id}
              title={collapsed ? label : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: collapsed ? "5px 6px" : "4px 6px",
                borderRadius: 6,
                marginBottom: 1,
                justifyContent: collapsed ? "center" : "flex-start",
                background: isActive ? T.brandSoft : "transparent",
                color: isActive ? T.brand : th.textSec,
                fontSize: 9,
                fontWeight: isActive ? 600 : 400,
                cursor: "default",
              }}
            >
              <span style={{ fontSize: 11 }}>{icon}</span>
              {!collapsed && label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SATopbar({ dark, active = "subscriptions", viewport = "desktop", onMenuToggle }) {
  const th = useTheme(dark);
  const mob = viewport === "mobile";
  const activeLabel = SA_NAV_ITEMS.find((item) => item[1] === active)?.[2] || active;

  return (
    <div
      style={{
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 12px",
        borderBottom: `1px solid ${th.border}`,
        background: dark ? "rgba(17,19,24,0.97)" : "rgba(255,255,255,0.97)",
        flexShrink: 0,
      }}
    >
      <Row gap={8}>
        {mob && onMenuToggle && (
          <button
            type="button"
            onClick={onMenuToggle}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: th.textSec, padding: 4 }}
          >
            ☰
          </button>
        )}
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 5,
            background: T.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
          }}
        >
          ⚡
        </div>
        {!mob && <span style={{ fontSize: 10, fontWeight: 600, color: th.text }}>ZiniZ Super Admin</span>}
        {!mob && <span style={{ fontSize: 9, color: th.textMuted }}>· ZiniHub ›</span>}
        <span style={{ fontSize: 9, fontWeight: 500, color: T.brand }}>{activeLabel}</span>
      </Row>
      <Row gap={5}>
        <div
          style={{
            fontSize: 8,
            color: th.textMuted,
            background: th.muted,
            padding: "2px 7px",
            borderRadius: 99,
            border: `1px solid ${th.border}`,
          }}
        >
          🔐 Super Admin
        </div>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: T.brandSoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 700,
            color: T.brand,
          }}
        >
          SA
        </div>
      </Row>
    </div>
  );
}

/** SAShell layout — full-width topbar, ResponsiveShell body, no footer. */
export function SAAppShell({ dark, viewport = "desktop", active = "subscriptions", pageTitle, children }) {
  const title = pageTitle || SA_NAV_ITEMS.find((item) => item[1] === active)?.[2] || active;
  const [navOpen, setNavOpen] = useState(false);
  const mob = viewport === "mobile";

  return (
    <div style={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
      <ResponsiveShell
        dark={dark}
        viewport={viewport}
        pageTitle={title}
        navOpen={navOpen}
        onNavToggle={setNavOpen}
        fullHeader={
          <SATopbar
            dark={dark}
            active={active}
            viewport={viewport}
            onMenuToggle={mob ? () => setNavOpen((open) => !open) : undefined}
          />
        }
        sidebar={({ collapsed }) => <SASidebar dark={dark} active={active} collapsed={collapsed} />}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: "10px 12px",
            background: dark ? T.dCanvas : T.lCanvas,
          }}
        >
          {children}
        </div>
      </ResponsiveShell>
    </div>
  );
}

/** @deprecated Use SAAppShell — kept for gradual migration */
export function SAShell(props) {
  return <SAAppShell {...props} />;
}
